/**
 * 「未註冊者要被導到哪裡」的決策純函式。
 *
 * 抽出來的原因：LineLoginProvider 的 401 分支同時牽涉 liff、axios、
 * react-router 三層副作用，難以直接測試。決策本身是純邏輯，抽成函式後
 * 可比照本 repo 既有的 util 測試慣例覆蓋各種路徑組合。
 */

/**
 * 這些路徑有自己的未註冊處理流程（頁內完成註冊 / 綁定），
 * 不可被外部導流搶走。與 LineLoginProvider 既有的四個旗標一致。
 */
const SELF_HANDLED_PATH_PATTERNS = [
  /^\/register(\/|$)/,
  /^\/onboarding(\/|$)/,
  /^\/sms-invite(\/|$)/,
  /^\/account\/addAccount(\/|$)/,
];

export const isSelfHandledUnregisteredPath = (pathname: string): boolean =>
  SELF_HANDLED_PATH_PATTERNS.some((re) => re.test(pathname));

export interface ShopEntryRedirectInput {
  /** 目前路徑（不含 query）。 */
  pathname: string;
  /** 已攜帶的瓦斯行邀請碼；無則 null。 */
  shopCode: string | null;
  /** 已攜帶的瓦斯行 id；無則 null。 */
  supplierId: string | null;
}

/**
 * 未註冊者若帶著瓦斯行邀請碼進站（例如從該瓦斯行 OA 的 rich menu 點進來），
 * 導向該瓦斯行的傳單註冊流程；否則回 null 讓呼叫端維持既有導流。
 *
 * shopCode 與 supplierId 必須同時存在才導流：兩者的權威來源是同一個
 * OA 的 supplier_line_settings，後端一定成對寫進深連結網址，不會只給
 * 其中一個。只有其中一個有值，代表這不是本次從 OA 進站帶來的參數，
 * 而是舊連結留在同一分頁 sessionStorage 裡的殘渣。半套參數會讓
 * Onboarding 的 isFlyerKnown 判斷錯誤（沒有 supplier 就跳過老客戶比對、
 * 把既有客戶當成新客戶），與其用不可信的殘渣觸發錯誤流程，不如忽略它、
 * 回 null 讓呼叫端退回既有導流。
 */
export const resolveShopEntryRedirect = ({
  pathname,
  shopCode,
  supplierId,
}: ShopEntryRedirectInput): string | null => {
  if (!shopCode || !supplierId) return null;
  if (isSelfHandledUnregisteredPath(pathname)) return null;

  const params = new URLSearchParams();
  params.append("supplier", supplierId);
  params.append("shopCode", shopCode);
  return `/onboarding/flyer?${params.toString()}`;
};

/**
 * liff.login() 是否需要明確帶 redirectUri。
 *
 * 不帶時 LINE 登入完會回到 LIFF endpoint 根網址（`/`），query／深連結路徑全部掉光。
 * sessionStorage 在 LINE webview 的 login round-trip 後也不一定讀得到 callbackPathname，
 * 使用者就會先看到 Welcome（像 fallback），再被導去真正目標 → 多次 loading。
 *
 * - /sms-invite、帶 shopCode：必須保住 query
 * - /home、/kiosk 等深連結：必須保住路徑（rich menu 常見）
 * - 根路徑 `/`：維持不帶 redirectUri（本來就停在 endpoint）
 */
export const shouldPreserveQueryOnLogin = (
  pathname: string,
  search: string,
): boolean => {
  if (/^\/sms-invite(\/|$)/.test(pathname)) return true;
  const params = new URLSearchParams(search);
  // LIFF 深連結進站時目的地放在 ?liff.state=（此刻 pathname 還是 "/"）。
  // 2026-09-03 診斷事件實錘：登入 session 過期時這裡回 false → liff.login() 不帶
  // redirectUri → LINE 登入來回後 liff.state 永久丟失（回跳網址只剩 code/state），
  // 深連結被吃掉直接進首頁——「時好時壞」的真因（session 還活著就不用繞登入、就成功）。
  // 帶著完整網址回跳，目的地才活得過登入來回。
  if ((params.get("liff.state") ?? "").trim() !== "") return true;
  // 家庭邀請碼（付款 QR / 邀請連結）：未註冊者過 LINE login 後 sessionStorage 讀不到，
  // 必須把 ?invite_code= 保留在網址上，登入後才攔得到、才能「註冊後立即加入家庭」。
  if ((params.get("invite_code") ?? "").trim() !== "") return true;
  const shopCode = params.get("shopCode");
  if ((shopCode ?? "").trim() !== "") return true;
  // 非根路徑的深連結一律帶回原 URL，避免登入後掉到 Welcome
  if (pathname && pathname !== "/") return true;
  return false;
};
